import { useMemo, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthCardLayout } from "../components/AuthCardLayout";
import { betterAuthRequest } from "../lib/auth";
import { buildResetPasswordRedirectUrl } from "../lib/invitation-flow";

export function ForgotPasswordPage() {
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const redirectPath = searchParams.get("redirect") || "/login";
  const inviteEmail = searchParams.get("email")?.trim().toLowerCase() ?? "";
  const [email, setEmail] = useState(inviteEmail);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const redirectTo = buildResetPasswordRedirectUrl({
        origin: window.location.origin,
        redirectPath,
        email: inviteEmail || normalizedEmail,
      });
      await betterAuthRequest("/request-password-reset", {
        method: "POST",
        body: JSON.stringify({
          email: normalizedEmail,
          redirectTo,
        }),
      });

      setNotice(
        "If an account exists for that email, a password reset link is on its way. Check your inbox.",
      );
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to send reset email",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCardLayout
      heroEyebrow="Account recovery"
      heroTitle="Reset your Datascope password."
      heroDescription="Enter the email address associated with your account and we will send you a link to choose a new password."
      formEyebrow="Recovery"
      formTitle="Forgot password"
      formDescription="We will email you a link to reset your password."
    >
      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Email</span>
          <input
            type="email"
            value={email}
            disabled={submitting}
            onChange={(event) => setEmail(event.target.value)}
            className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary"
            placeholder="satya@company.com"
            required
          />
        </label>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {notice ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {notice}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-[#005f8a] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Sending reset link..." : "Send reset link"}
        </button>

        <div className="text-center text-sm text-slate-600">
          <Link
            to={redirectPath}
            className="font-medium text-slate-900 underline-offset-4 hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </form>
    </AuthCardLayout>
  );
}
